import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PremiumCard } from '../premium';
import { formatDistanceM, primaryDistanceM } from '../../lib/cardioDraft';
import { formatVolume, workoutSetCount } from '../../lib/workoutStats';
import type { Workout } from '../../types';

export function WorkoutTimelineCard({
  workout,
  imperial,
  menuOpen,
  onMenuToggle,
  onDelete,
}: {
  workout: Workout;
  imperial: boolean;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onDelete: () => void;
}) {
  const nav = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const sets = workoutSetCount(workout);
  const volume = formatVolume(workout, imperial);
  const dist = formatDistanceM(primaryDistanceM(workout), imperial);
  const date = new Date(workout.startedAt);
  const dateLabel = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const duration = workout.durationMinutes && workout.durationMinutes > 0 ? `${workout.durationMinutes} min` : null;
  const kcal =
    workout.estimatedCaloriesBurned != null && workout.estimatedCaloriesBurned > 0
      ? `${workout.estimatedCaloriesBurned} kcal`
      : null;

  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onMenuToggle();
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen, onMenuToggle]);

  return (
    <PremiumCard className="!p-0 overflow-hidden">
      <button
        type="button"
        onClick={() => nav(`/workouts/history/${workout.id}`)}
        className="flex w-full items-center gap-3 p-3.5 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold text-[var(--text-primary)]">{workout.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[var(--text-muted)]">
            <span>{dateLabel}</span>
            {workout.importSource === 'apple_health' ? (
              <span className="rounded-full border border-pink-400/25 bg-pink-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-pink-200">
                Apple Health
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-medium text-[var(--text-secondary)]">
            {duration ? <span>{duration}</span> : null}
            {dist ? <span>{dist}</span> : null}
            {sets > 0 ? <span>{sets} sets</span> : null}
            {volume ? <span>{volume}</span> : null}
            {kcal ? <span>{kcal}</span> : null}
          </div>
        </div>
        <span className="shrink-0 text-lg text-[var(--text-muted)]">›</span>
      </button>
      <div className="relative flex justify-end border-t border-white/5 px-2 py-0.5">
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle();
            }}
            aria-label="Options"
            className="rounded-lg px-2 py-1 text-lg text-[var(--text-muted)]"
          >
            ⋯
          </button>
          {menuOpen ? (
            <div className="absolute bottom-full right-0 z-10 mb-1 min-w-[120px] overflow-hidden rounded-xl border border-white/10 bg-[#151f33] py-1 shadow-xl">
              <button
                type="button"
                onClick={() => nav(`/workouts/edit/${workout.id}`)}
                className="block w-full px-3 py-2 text-left text-xs text-[var(--text-secondary)] hover:bg-white/[0.06]"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="block w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-red-500/10"
              >
                Delete
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </PremiumCard>
  );
}
